import { FC } from 'react';
import { Box, Drawer, DrawerProps, Typography, IconButton, Button } from '@mui/material';
import { ClearOutlined as ClearOutlinedIcon } from '@mui/icons-material';

import { StickyBottomAppBar } from '../../../../../components/AppBar';
import { useConfirmDialogContext } from '../../../../../hooks/Contexts/ConfirmDialogContext';
import { useInfoMessage } from '../../../../../hooks/useInfoMessage';
import ApiService from '../../../../../ApiService';
import Block from '../../../../../components/Block';
import Column from '../../../../../components/Column';
import Row from '../../../../../components/Row';
import VerticalDivider from '../../../../../components/Divider/VerticalDivider';

import { CampaignChangeRequest } from '../../../../../types/Campaign';
import { useCampaignPageContext } from '../../../../../hooks/Contexts/Pages/CampaignPageContext';
import { useUserContext } from '../../../../../hooks/Contexts/UserContext';
import CampaignChangeRequestStatus from '../../../../../features/Campaign/CampaignChangeRequestStatus';
import CampaignChangeRequestTable from '../Table/CampaignChangeRequestTable';

type CampaignChangeRequestDrawerActionCallback = (campaignChangeRequest?: CampaignChangeRequest) => void;

interface CampaignChangeRequestDrawerProps extends DrawerProps {
    campaignChangeRequest: CampaignChangeRequest;
    onCancel?: CampaignChangeRequestDrawerActionCallback;
    onApprove?: CampaignChangeRequestDrawerActionCallback;
    onDecline?: CampaignChangeRequestDrawerActionCallback;
}

const CampaignChangeRequestDrawer: FC<CampaignChangeRequestDrawerProps> = ({
    campaignChangeRequest,
    onCancel,
    onApprove,
    onDecline,
    ...props
}) => {
    const { showConfirm, hideConfirm } = useConfirmDialogContext();
    const { showInfoMessage } = useInfoMessage();

    const { userContext } = useUserContext();
    const { campaign } = useCampaignPageContext();

    const handleOnCancel = (): void => {
        onCancel?.(campaignChangeRequest);
    };

    const handleOnApprove = (): void => {
        showConfirm({
            title: 'Are you sure you want to approve this campaign change request?',
            onOk: () => {
                hideConfirm();

                if (campaignChangeRequest.campaignId && campaignChangeRequest.id) {
                    ApiService.updateCampaignChangeRequestComplete(
                        campaignChangeRequest.campaignId,
                        campaignChangeRequest.id
                    )
                        .then((response) => {
                            onApprove?.(campaignChangeRequest);

                            showInfoMessage('success', 'Campaign change request has been approved.');
                        })
                        .catch((error) => {
                            showInfoMessage('error', error.message);
                        });
                }
            },
            okText: 'Approve Campaign Change Request',
            okColor: 'success',
        });
    };

    const handleOnDecline = (): void => {
        showConfirm({
            title: 'Are you sure you want to decline this campaign change request?',
            onOk: () => {
                hideConfirm();

                if (campaignChangeRequest.campaignId && campaignChangeRequest.id) {
                    ApiService.declineCampaignChangeRequest(campaignChangeRequest.campaignId, campaignChangeRequest.id)
                        .then(() => {
                            onDecline?.(campaignChangeRequest);

                            showInfoMessage('success', 'Campaign change request has been declined.');
                        })
                        .catch((error) => {
                            showInfoMessage('error', error.message);
                        });
                }
            },
            okText: 'Decline Campaign Change Request',
            okColor: 'error',
        });
    };

    return (
        <Drawer
            anchor="right"
            {...props}
            sx={{
                width: 7 / 12,
                flexShrink: 0,
                zIndex: (theme) => theme.zIndex.drawer + 1,

                '& .MuiDrawer-paper': {
                    width: 7 / 12,
                    boxSizing: 'border-box',
                },
            }}
        >
            <Column
                justifyContent="space-between"
                sx={{
                    minHeight: '480px',
                    height: '100%',
                }}
            >
                <Box>
                    <Box
                        component="header"
                        sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                            paddingX: 3,
                            paddingY: 2,
                        }}
                    >
                        <Row justifyContent="space-between" alignItems="center" spacing={2}>
                            <Row gap={1} alignItems="center">
                                <Typography variant="h6">Review Change Request</Typography>

                                <VerticalDivider />

                                <CampaignChangeRequestStatus value={campaignChangeRequest.status || ''} />
                            </Row>

                            <IconButton onClick={handleOnCancel} edge="end">
                                <ClearOutlinedIcon fontSize="medium" color="action" />
                            </IconButton>
                        </Row>
                    </Box>

                    <Block px={4} py={3}>
                        <CampaignChangeRequestTable campaign={campaign} campaignChangeRequest={campaignChangeRequest} />
                    </Block>
                </Box>

                {(userContext.isAdmin() || userContext.isOpsAdmin()) && campaignChangeRequest?.status === 'Pending' && (
                    <StickyBottomAppBar>
                        <Row justifyContent="flex-end" gap={1}>
                            <Button variant="outlined" onClick={handleOnCancel}>
                                Cancel
                            </Button>
                            <Button variant="contained" color="error" onClick={handleOnDecline}>
                                Decline
                            </Button>
                            <Button variant="contained" color="primary" onClick={handleOnApprove}>
                                Approve
                            </Button>
                        </Row>
                    </StickyBottomAppBar>
                )}
            </Column>
        </Drawer>
    );
};

export default CampaignChangeRequestDrawer;
